import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { motion } from 'framer-motion';
import { Plus, QrCode, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const SettingsManagement = () => {
  const [upiUrl, setUpiUrl] = useState('');
  const [executiveName, setExecutiveName] = useState('');
  const [executiveWhatsapp, setExecutiveWhatsapp] = useState('');
  const [availabilitySlots, setAvailabilitySlots] = useState([]);
  const [counselorName, setCounselorName] = useState('');
  const [counselorWhatsapp, setCounselorWhatsapp] = useState('');
  const [counselorSlots, setCounselorSlots] = useState([]);
  const [qrFile, setQrFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setUpiUrl(response.data?.upi_qr_code_url || '');
      setExecutiveName(response.data?.executive_name || '');
      setExecutiveWhatsapp(response.data?.executive_whatsapp || '');
      setAvailabilitySlots(response.data?.availability_slots || []);
      setCounselorName(response.data?.counselor_name || '');
      setCounselorWhatsapp(response.data?.counselor_whatsapp || '');
      setCounselorSlots(response.data?.counselor_availability_slots || []);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSlotChange = (index, value) => {
    setAvailabilitySlots((prev) => prev.map((slot, idx) => (idx === index ? value : slot)));
  };

  const handleAddSlot = () => {
    setAvailabilitySlots((prev) => [...prev, '']);
  };

  const handleRemoveSlot = (index) => {
    setAvailabilitySlots((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleCounselorSlotChange = (index, value) => {
    setCounselorSlots((prev) => prev.map((slot, idx) => (idx === index ? value : slot)));
  };

  const handleAddCounselorSlot = () => {
    setCounselorSlots((prev) => [...prev, '']);
  };

  const handleRemoveCounselorSlot = (index) => {
    setCounselorSlots((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleQrFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setQrFile(file);
    }
  };

  const handleQrUpload = async () => {
    if (!qrFile) {
      toast.error('Please select a QR image to upload');
      return;
    }

    const formData = new FormData();
    formData.append('file', qrFile);

    setUploading(true);
    try {
      const response = await axios.post(`${API}/settings/upi-qr-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUpiUrl(response.data?.upi_qr_code_url || response.data?.url || '');
      toast.success('QR code uploaded successfully');
      setQrFile(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload QR code');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, {
        upi_qr_code_url: upiUrl,
        executive_name: executiveName,
        executive_whatsapp: executiveWhatsapp,
        availability_slots: availabilitySlots.map((slot) => slot.trim()).filter(Boolean),
        counselor_name: counselorName,
        counselor_whatsapp: counselorWhatsapp,
        counselor_availability_slots: counselorSlots.map((slot) => slot.trim()).filter(Boolean)
      });
      toast.success('Settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="admin-settings-loading">
        <div className="text-lg text-slate-600">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-2" data-testid="admin-settings-title">
          Settings
        </h1>
        <p className="text-lg text-slate-600" data-testid="admin-settings-subtitle">
          Manage payment QR code and platform details
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-violet-600" />
              UPI & Executive Settings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="executive-name">Executive Name</Label>
                <Input
                  id="executive-name"
                  placeholder="e.g., Priya Sharma"
                  value={executiveName}
                  onChange={(e) => setExecutiveName(e.target.value)}
                  data-testid="admin-settings-executive-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="executive-whatsapp">Executive WhatsApp Number</Label>
                <Input
                  id="executive-whatsapp"
                  placeholder="e.g., +91 98765 43210"
                  value={executiveWhatsapp}
                  onChange={(e) => setExecutiveWhatsapp(e.target.value)}
                  data-testid="admin-settings-executive-whatsapp-input"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Availability Time Slots</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddSlot}
                    data-testid="admin-settings-add-slot-button"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Slot
                  </Button>
                </div>
                <div className="space-y-2" data-testid="admin-settings-slot-list">
                  {availabilitySlots.length === 0 && (
                    <p className="text-sm text-slate-500">Add time slots like "Mon-Sat, 10 AM - 7 PM"</p>
                  )}
                  {availabilitySlots.map((slot, index) => (
                    <div key={`${slot}-${index}`} className="flex items-center gap-2">
                      <Input
                        placeholder="e.g., Mon-Fri, 10 AM - 6 PM"
                        value={slot}
                        onChange={(e) => handleSlotChange(index, e.target.value)}
                        data-testid={`admin-settings-slot-input-${index}`}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => handleRemoveSlot(index)}
                        data-testid={`admin-settings-remove-slot-${index}`}
                      >
                        <Trash2 className="w-4 h-4 text-rose-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="counselor-name">Counselor Name</Label>
                  <Input
                    id="counselor-name"
                    placeholder="e.g., Ankit Verma"
                    value={counselorName}
                    onChange={(e) => setCounselorName(e.target.value)}
                    data-testid="admin-settings-counselor-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="counselor-whatsapp">Counselor WhatsApp Number</Label>
                  <Input
                    id="counselor-whatsapp"
                    placeholder="e.g., +91 98765 43210"
                    value={counselorWhatsapp}
                    onChange={(e) => setCounselorWhatsapp(e.target.value)}
                    data-testid="admin-settings-counselor-whatsapp-input"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Counselor Availability Slots</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAddCounselorSlot}
                      data-testid="admin-settings-counselor-add-slot-button"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Slot
                    </Button>
                  </div>
                  <div className="space-y-2" data-testid="admin-settings-counselor-slot-list">
                    {counselorSlots.length === 0 && (
                      <p className="text-sm text-slate-500">Add counselor availability like "Mon-Fri, 10 AM - 6 PM"</p>
                    )}
                    {counselorSlots.map((slot, index) => (
                      <div key={`${slot}-${index}`} className="flex items-center gap-2">
                        <Input
                          placeholder="e.g., Mon-Fri, 10 AM - 6 PM"
                          value={slot}
                          onChange={(e) => handleCounselorSlotChange(index, e.target.value)}
                          data-testid={`admin-settings-counselor-slot-input-${index}`}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => handleRemoveCounselorSlot(index)}
                          data-testid={`admin-settings-counselor-remove-slot-${index}`}
                        >
                          <Trash2 className="w-4 h-4 text-rose-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4 space-y-2">
                <Label htmlFor="upi-qr-upload">Upload UPI QR Code Image</Label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    id="upi-qr-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleQrFileChange}
                    data-testid="admin-settings-qr-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleQrUpload}
                    disabled={uploading}
                    data-testid="admin-settings-qr-upload-button"
                  >
                    {uploading ? 'Uploading...' : 'Upload'}
                  </Button>
                </div>
                <p className="text-xs text-slate-500">Upload a PNG/JPG QR image to auto-fill the URL.</p>
              </div>

              <div className="text-xs text-slate-500">
                QR code URL is generated automatically after upload.
              </div>

              <Button
                type="submit"
                className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full"
                disabled={saving}
                data-testid="admin-settings-save-button"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-xl">
          <CardHeader>
            <CardTitle>Preview</CardTitle>
          </CardHeader>
          <CardContent>
            {upiUrl ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center"
              >
                <div className="bg-white border-4 border-violet-200 rounded-2xl p-4 inline-block">
                  <img
                    src={upiUrl}
                    alt="UPI QR Code"
                    className="w-64 h-64 mx-auto"
                    data-testid="admin-settings-upi-preview"
                  />
                </div>
                <p className="mt-4 text-sm text-slate-600">This is the QR code students will scan.</p>
                {(executiveName || executiveWhatsapp) && (
                  <div className="mt-4 text-sm text-slate-600" data-testid="admin-settings-executive-preview">
                    <p className="font-medium text-slate-900">Executive Contact</p>
                    <p>{executiveName || 'Name not set'}</p>
                    <p>{executiveWhatsapp || 'WhatsApp not set'}</p>
                  </div>
                )}
                {(counselorName || counselorWhatsapp) && (
                  <div className="mt-4 text-sm text-slate-600" data-testid="admin-settings-counselor-preview">
                    <p className="font-medium text-slate-900">Counselor Contact</p>
                    <p>{counselorName || 'Name not set'}</p>
                    <p>{counselorWhatsapp || 'WhatsApp not set'}</p>
                  </div>
                )}
              </motion.div>
            ) : (
              <div className="text-center py-12 text-slate-500" data-testid="admin-settings-empty">
                Add a QR code URL to preview it here.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SettingsManagement;